import {
  getFirestore,
  collection,
  addDoc,
  doc,
  updateDoc,
  deleteDoc,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import _ from "lodash";
// Create Deck Funcitons ===============================================

/**
 * Adds Cards to a deck.
 * @param {array} allCards - all available cards.
 * @param {object} selectedCard - selected card to add to deck.
 * @param {array} newDeck - the current deck.
 * @returns {function} setNewDeck - updated deck.
 */
export const addToDeck = (
  allCards,
  selectedCard,
  newDeck,
  setNewDeck,
  setMessage,
  setError
) => {
  // Count how many of this card already exist in the deck
  const countInDeck = newDeck.filter(
    (c) => c.name === selectedCard.name
  ).length;

  // Count how many of this card the user owns
  // const countInInventory = allCards.filter(
  // 	(c) => c.name === selectedCard.name
  // ).length;
  const countInInventory = selectedCard.amountOwned;

  // Don't add if they reached max allowed or don't have more copies
  if (countInDeck >= selectedCard.max) {
    setMessage("You've added the max amount of this card.");
    setError(true);
    return;
  }
  if (countInDeck >= countInInventory) {
    setMessage("Can't add more of this card.");
    setError(true);
    return;
  }
  if (newDeck.length >= 40) {
    setMessage("Your deck is full.");
    setError(true);
    return;
  }

  setNewDeck((prev) => [...prev, selectedCard]);
};

/**
 * Removes a single instance of a card from the deck.
 * @param {object} selectedCard - Card to remove.
 * @param {array} newDeck - Current deck array.
 * @returns {function} setNewDeck - Updated deck.
 */
export const removeFromDeck = (selectedCard, newDeck, setNewDeck) => {
  const index = newDeck.findIndex((c) => c.name === selectedCard.name);

  if (index !== -1) {
    const updatedDeck = [...newDeck];
    updatedDeck.splice(index, 1);
    setNewDeck(updatedDeck);
  } else {
    console.log("Card not found in deck.");
  }
};

/**
 * Saves the deck to Firebase.
 * @param {string} deckName - Name of the deck.
 * @param {object} character - Character associated with the deck.
 * @param {array} deck - Array of cards in the deck.
 * @param {function} resetDeck - Function to reset the deck.
 * @returns {string} message - success message.
 */
export const saveDeckToFirebase = async (
  userData,
  deckName,
  deckColor,
  deck,
  deckId,
  resetDeck
) => {
  console.log("attempting to save deck");
  //   const auth = getAuth();
  //   console.log("got auth");
  //   const user = auth.currentUser;
  let message = "";

  if (!userData) {
    console.log("User not authenticated or deck is invalid.");
    message = "User not authenticated or deck is invalid.";
    return { success: false, message };
  }

  if (!deck || deck.length !== 40) {
    console.log("Deck is invalid or empty.");
    message = "Can't save this deck. It does not have 40 cards.";
    return { success: false, message };
  }

  if (!deckName || deckName.trim() === "") {
    console.log("Deck name is invalid.");
    message = "Please add a Name to your deck.";
    return { success: false, message };
  }

  if (!deckColor) {
    message = "Please assign a Color to your deck.";
    return message;
  }

  const db = getFirestore();
  try {
    if (deckId) {
      const docRef = doc(db, "users", userData.id, "decks", deckId);
      await updateDoc(docRef, {
        name: deckName,
        // character: character,
        color: deckColor,
        cards: deck,
        updatedAt: new Date(),
      });
      console.log("Deck updated in Firestore!");
      message = "Deck updated Successfully!";
      resetDeck();

      return { success: true, message };
    } else {
      await addDoc(collection(db, "users", userData.id, "decks"), {
        name: deckName,
        // character: character,
        color: deckColor,
        cards: deck,
        createdAt: new Date(),
      });
      console.log("Deck saved to Firestore!");
      message = "Deck saved Successfully!";
      resetDeck();
      return { success: true, message };
    }
  } catch (error) {
    console.error("Error saving deck:", error);
    message = "Error saving deck, please try again.";
    return { success: false, message };
  }
};

export const deleteDeckFromFirebase = async (userData, deckId, resetDeck) => {
  let message = "";
  //   const auth = getAuth();
  //   const user = auth.currentUser;
  if (!userData) {
    console.log("User not authenticated or deck is invalid.");
    return;
  }
  const db = getFirestore();
  try {
    const deckRef = doc(db, "users", userData.id, "decks", deckId);
    await deleteDoc(deckRef);
    console.log("Deck deleted from Firestore!");
    message = "Deck deleted Successfully!";
    resetDeck();
    return message;
  } catch (error) {
    console.error("Error deleting deck:", error);
    message = "Error deleting deck, please try again.";
    return message;
  }
};

export const shuffle = (array) => {
  const copy = [...array];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
};

export const createDeck = () => {
  // Sample deck generator logic
  return Array.from({ length: 60 }, (_, i) => ({
    id: `card-${i + 1}`,
    type: "generic",
    name: `Card ${i + 1}`,
  }));
};

export const parseCardEffect = (card) => {
  const combinedText = (card.description + " " + card.effect).toLowerCase();

  const isAffliction =
    /reduce|decrease|weaken|lower|afflict|damage|poison|lose|remove|takes/.test(
      combinedText
    );
  const isAid = /restore|increase|heal|boost|raise|enhance|aid/.test(
    combinedText
  );
  const hasTurnLoss = /turn/.test(card.areaAffected.join().toLowerCase());

  const amountMatch = card.effect.match(/(\d+)/);
  const amount = amountMatch ? parseInt(amountMatch[1]) : 1;

  const turnsMatch = card.turnsActive?.match(/(\d+)/);
  const duration = turnsMatch ? parseInt(turnsMatch[1]) : 0;

  return {
    isAffliction,
    imageUrl: card.imageUrl,
    isAid,
    amount: isAffliction ? (hasTurnLoss ? amount : -amount) : amount,
    duration,
    affectedAreas: card.areaAffected || [],
  };
};

// export function applyTurnBuffs(character) {
//   const stats = ["health", "defense", "attack", "accuracy"];
//   const appliedBuffs = [];

//   stats.forEach((stat) => {
//     const buffs = character.buffs?.[stat];
//     if (!Array.isArray(buffs)) return;

//     const updatedBuffs = [];

//     for (const buff of buffs) {
//       const isInstantAttack =
//         (stat === "attack" || stat === "accuracy") && buff.duration === 0;

//       // Apply only for health and defense
//       if (
//         (stat === "health" || stat === "defense") &&
//         typeof buff.amt === "number"
//       ) {
//         character[stat] += buff.amt;
//         appliedBuffs.push({ stat, amt: buff.amt });
//         console.log(
//           `🟢 ${character.name} gains ${buff.amt} ${stat} (new ${stat}: ${character[stat]})`
//         );
//       }

//       if (!isInstantAttack) {
//         buff.duration -= 1;
//       }

//       if (isInstantAttack || buff.duration > 0) {
//         updatedBuffs.push(buff);
//       }
//     }

//     character.buffs[stat] = updatedBuffs;
//   });

//   return { character, appliedBuffs };
// }

// !V2

// export function applyTurnBuffs(character) {
//   const charCopy = _.cloneDeep(character); // NEW LINE

//   const stats = ["health", "defense", "attack", "accuracy"];
//   const appliedBuffs = [];

//   stats.forEach((stat) => {
//     const buffs = charCopy.buffs?.[stat];
//     if (!Array.isArray(buffs)) return;

//     const updatedBuffs = [];

//     for (const buff of buffs) {
//       const isInstantAttack =
//         (stat === "attack" || stat === "accuracy") && buff.duration === 0;

//       if (
//         (stat === "health" || stat === "defense") &&
//         typeof buff.amt === "number"
//       ) {
//         charCopy[stat] += buff.amt;
//         appliedBuffs.push({ stat, amt: buff.amt });
//       }

//       if (!isInstantAttack) {
//         buff.duration -= 1;
//       }

//       if (isInstantAttack || buff.duration > 0) {
//         updatedBuffs.push(buff);
//       }
//     }

//     charCopy.buffs[stat] = updatedBuffs;
//   });

//   return { character: charCopy, appliedBuffs };
// }

//! V3
export function applyTurnBuffs(character) {
  const charCopy = _.cloneDeep(character);
  const stats = ["health", "defense", "attack", "accuracy"];
  const appliedBuffs = [];

  stats.forEach((stat) => {
    const buffs = charCopy.buffs?.[stat];
    if (!Array.isArray(buffs)) return;

    const updatedBuffs = [];

    for (const buff of buffs) {
      const isInstantAttack =
        (stat === "attack" || stat === "accuracy") && buff.duration === 0;

      if (
        (stat === "health" || stat === "defense") &&
        typeof buff.amt === "number"
      ) {
        charCopy[stat] += buff.amt;

        // Clamp values to minimum 0
        if (charCopy[stat] < 0) charCopy[stat] = 0;

        appliedBuffs.push({ stat, amt: buff.amt });
      }

      if (!isInstantAttack) {
        buff.duration -= 1;
      }

      if (isInstantAttack || buff.duration > 0) {
        updatedBuffs.push(buff);
      }
    }

    charCopy.buffs[stat] = updatedBuffs;
  });

  // Apply specialty rules
  if (charCopy.health <= 0) {
    charCopy.isDead = true;
    charCopy.health = 0;
  }

  if (charCopy.defense <= 0) {
    charCopy.defense = 0;
    charCopy.stance = "attack";
  }

  return { character: charCopy, appliedBuffs };
}

export const buildBuffMessage = (
  playerName,
  appliedBuffs,
  hasTurnLoss,
  lostMoves
) => {
  const cap = (str) => str.charAt(0).toUpperCase() + str.slice(1);
  let msg = `${playerName} is buffed`;

  if (appliedBuffs.length) {
    const summary = appliedBuffs
      .map((b) => `+${b.amt} ${cap(b.stat)}`)
      .join(" and ");
    msg += ` and received ${summary}.`;
  } else {
    msg = "";
  }

  if (hasTurnLoss && lostMoves > 0) {
    msg += ` However, they lost ${lostMoves > 2 ? 2 : lostMoves} move${
      lostMoves > 1 ? "s" : ""
    } due to a debuff.`;
  }

  return msg;
};
